import { useState, useEffect, useCallback, useRef } from 'react';
import { booksApi, Book, BookQueryParams } from '@/services/catalog-api';

interface CatalogBooksParams {
  query: string;
  genreId: number | null;
  /** An author picked from the search suggestions. */
  authorId?: number | null;
  availableOnly: boolean;
  ordering: string;
}

interface UseCatalogBooksReturn {
  books: Book[];
  isFetching: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: () => void;
  retry: () => void;
}

export function useCatalogBooks(params: CatalogBooksParams): UseCatalogBooksReturn {
  const [books, setBooks] = useState<Book[]>([]);
  // Start true to show skeleton before first fetch resolves
  const [isFetching, setIsFetching] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pageRef = useRef(1);
  // Single shared signal: cancelled before every new fetch to prevent stale appends
  const activeSignalRef = useRef<{ cancelled: boolean } | null>(null);

  const fetchPage = useCallback(
    async (page: number, append: boolean, signal: { cancelled: boolean }) => {
      setIsFetching(true);
      setError(null);

      const queryParams: BookQueryParams = {
        page,
        page_size: 20,
        ordering: params.ordering,
      };
      if (params.query.trim()) queryParams.search = params.query.trim();
      if (params.genreId !== null) queryParams.genre = params.genreId;
      if (params.authorId) queryParams.author = params.authorId;
      if (params.availableOnly) queryParams.available_only = true;

      try {
        const response = await booksApi.getBooks(queryParams);
        if (signal.cancelled) return;
        setBooks((prev) => (append ? [...prev, ...response.results] : response.results));
        setHasMore(!!response.next);
      } catch (err) {
        if (signal.cancelled) return;
        console.error('[useCatalogBooks] Failed to fetch page:', err);
        setError('Failed to load books. Please try again.');
      } finally {
        if (!signal.cancelled) setIsFetching(false);
      }
    },
    [params.query, params.genreId, params.authorId, params.availableOnly, params.ordering]
  );

  // Reset and fetch page 1 whenever filter params change
  useEffect(() => {
    // Cancel any in-flight fetch (loadMore, retry, or previous filter fetch)
    if (activeSignalRef.current) activeSignalRef.current.cancelled = true;
    const signal = { cancelled: false };
    activeSignalRef.current = signal;

    pageRef.current = 1;
    setBooks([]);
    setHasMore(false);
    fetchPage(1, false, signal);

    return () => {
      signal.cancelled = true;
    };
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (isFetching || !hasMore) return;
    // Cancel any previous in-flight fetch before starting the next page
    if (activeSignalRef.current) activeSignalRef.current.cancelled = true;
    const signal = { cancelled: false };
    activeSignalRef.current = signal;
    const nextPage = pageRef.current + 1;
    pageRef.current = nextPage;
    fetchPage(nextPage, true, signal);
  }, [isFetching, hasMore, fetchPage]);

  const retry = useCallback(() => {
    // Cancel any previous in-flight fetch before retrying
    if (activeSignalRef.current) activeSignalRef.current.cancelled = true;
    const signal = { cancelled: false };
    activeSignalRef.current = signal;
    fetchPage(pageRef.current, pageRef.current > 1, signal);
  }, [fetchPage]);

  return { books, isFetching, hasMore, error, loadMore, retry };
}
